import { useMemo } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { format } from 'date-fns';

interface UseDemoDateLockReturn {
  isDemoMode: boolean;
  isLocked: boolean;
  demoStartDate: string | null;
  demoEndDate: string | null;
  showLockToast: () => void;
}

/**
 * Hook to manage DEMO mode date locking
 *
 * Features:
 * - Detects DEMO environment from VITE_SHOW_DEMO
 * - Validates and provides DEMO date range from env vars
 * - Provides toast notification for locked state
 *
 * @returns {UseDemoDateLockReturn} DEMO lock state and utilities
 */
export const useDemoDateLock = (): UseDemoDateLockReturn => {
  const { toast } = useToast();

  const isDemoMode = import.meta.env.VITE_SHOW_DEMO === 'true';

  const { demoStartDate, demoEndDate, isValid } = useMemo(() => {
    if (!isDemoMode) {
      return { demoStartDate: null, demoEndDate: null, isValid: false };
    }

    const startDate = import.meta.env.VITE_DEMO_START_DATE;
    const endDate = import.meta.env.VITE_DEMO_END_DATE;

    if (!startDate || !endDate) {
      return { demoStartDate: null, demoEndDate: null, isValid: false };
    }

    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(startDate) || !dateRegex.test(endDate)) {
      console.error('[useDemoDateLock] Invalid date format. Expected YYYY-MM-DD');
      return { demoStartDate: null, demoEndDate: null, isValid: false };
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      console.error('[useDemoDateLock] Invalid dates provided');
      return { demoStartDate: null, demoEndDate: null, isValid: false };
    }

    if (start > end) {
      console.error('[useDemoDateLock] Start date must be before or equal to end date');
      return { demoStartDate: null, demoEndDate: null, isValid: false };
    }

    return { demoStartDate: startDate, demoEndDate: endDate, isValid: true };
  }, [isDemoMode]);

  const showLockToast = useMemo(() => {
    return () => {
      if (!demoStartDate || !demoEndDate) return;

      const formattedRange = `${format(new Date(demoStartDate), 'dd MMM yyyy')} - ${format(new Date(demoEndDate), 'dd MMM yyyy')}`;

      toast({
        title: 'DEMO Mode - Date Selection Locked',
        description: `This is a demonstration environment. Date range is fixed to ${formattedRange}.`,
        variant: 'default',
      });
    };
  }, [demoStartDate, demoEndDate, toast]);

  return {
    isDemoMode,
    isLocked: isDemoMode && isValid,
    demoStartDate,
    demoEndDate,
    showLockToast,
  };
};
